import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { REQUEST_ENTITY } from '../../../../constants/entity'
import {
  useInitData,
  useLinkLikeClick,
  useNotification,
  usePageBrowserTabTitle,
  usePageShowReload,
  useSelection,
} from '../../../../hooks'
import { hasDocFlowsSectionCreatePermission } from '../../../../utils/docFlow'
import { getRequestProfilePathname } from '../../../../utils/link'
import {
  TABLE_COLUMN_MAX_WIDTH,
  TABLE_COLUMN_MIN_WIDTH_SMALL,
  TABLE_COLUMN_RESIZE_CONFIG,
  TABLE_COLUMN_RESIZE_CONFIG_MAX_KEY,
  TABLE_COLUMN_RESIZE_CONFIG_MIN_KEY,
  useAsyncTable,
  useTableRefresh,
  useTableReloadAfterMassAction,
} from '../../../Table'
import {
  getRequestFilterStateOptions,
  getRequestPermanentFilters,
} from '../../Request.utils'
import { getExportOptions, getMassActions } from '../../Request.utils.actions'
import { useRequestLoadExtraTableData } from '../useRequestLoadExtraTableData'
import { useRequestTableActions } from '../useRequestTableActions'
import { useRequestTableAttachedRequests } from '../useRequestTableAttachedRequests'
import { useRequestTableDocFlows } from '../useRequestTableDocFlows'
import { useRequestTableHandleExport } from '../useRequestTableHandleExport'
import { useRequestTableItemAttachedFiles } from '../useRequestTableItemAttachedFiles'
import { useRequestTableItemUpdate } from '../useRequestTableItemUpdate'

import { REQUEST_NO_COLUMN_KEY } from './useRequestTablePage.constants'

export function useRequestTablePage({
  actionsSub,
  asyncTableOptions,
  entity = REQUEST_ENTITY,
  extraSelectionResetDeps = EMPTY_LIST,
  getPermanentFilters = getRequestPermanentFilters,
  handleClickEmailTag,
  pageTitle,
  setModal,
  sub = null,
  withoutBrowserTabTitle = false,
  onApplyTableConfig,
  onCloseModal,
  onReloadActiveItem,
}) {
  const { t } = useTranslation()

  const massActionsSub = actionsSub ?? sub

  const { handleAddNotification } = useNotification()

  const [isConfigOpen, setConfigOpen] = useState(false)
  const [isExtraEnabled, setExtraEnabled] = useState(true)

  const pageScrollWrapperRef = useRef(null)
  const pageContentRef = useRef(null)

  const asyncTable = useAsyncTable({
    ...asyncTableOptions,
    ...getRequestFilterStateOptions({
      getPermanentFilters,
      sub,
      transformInitialFilters: asyncTableOptions.transformInitialFilters,
    }),
    containerRef: pageScrollWrapperRef,
    entity,
    getPermanentFilters,
    sub,
  })

  const {
    columns,
    filterValues,
    handleReload: handleReloadTable,
    handleSetDense,
    isDense,
    isInitiated,
    isRefetching,
    items,
    permissions,
    preview,
    view,
  } = asyncTable

  const filterValuesWithExtra = {
    ...filterValues,
    ...(asyncTableOptions.extraFilters || null),
  }

  usePageBrowserTabTitle({
    isEnabled: !withoutBrowserTabTitle,
    isLoading: !isInitiated,
    pageTitle,
  })

  const selection = useSelection({
    data: items,
    pagination: asyncTable.pagination,
  })

  const {
    excludeIds,
    handleCleanSelection,
    handleUnselectAllPages,
    selectAllPages,
    selected,
    selectedIds,
  } = selection

  const { extra, handleReloadExtra } = useRequestLoadExtraTableData(
    isExtraEnabled ? items?.items : null
  )

  // A board over the hidden table shows none of its tags.
  function handleChangeExtraEnabled({ isEnabled }) {
    setExtraEnabled(isEnabled)
  }

  async function handleReload() {
    handleReloadExtra()

    return handleReloadTable()
  }

  async function handleReloadWithActiveItem(mutatedItemId) {
    onReloadActiveItem?.(mutatedItemId)

    return handleReload()
  }

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  const {
    handleChangeShouldReloadFilters,
    handleReloadTableAfterMassAction,
    shouldReloadFilters,
  } = useTableReloadAfterMassAction({
    filterValues,
    getPermanentFilters,
    sub,
    onReload: handleReload,
  })

  async function handleReloadAfterMassActionWithActiveItem() {
    onReloadActiveItem?.()

    return handleReloadTableAfterMassAction()
  }

  const { handleChangeVisibility } = useRequestTableItemUpdate({
    onAddNotification: handleAddNotification,
    onReload: handleReloadWithActiveItem,
  })

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const { availableSections, sections } = useInitData()

  useEffect(() => {
    handleCleanSelection()
  }, [...extraSelectionResetDeps, filterValues])

  usePageShowReload(handleReload)

  const canCreateDocFlow = hasDocFlowsSectionCreatePermission(sections)

  // Rows have no doc flow link permission, and their link permissions are off for
  // archived requests; the list-level one is admin/manager, as the link endpoints.
  const canConnectDocFlow = permissions.can_add_connected_requests

  const massActions = getMassActions({
    canCreateDocFlow,
    permissions,
    selectAllPages,
    selected,
    sub: massActionsSub,
    t,
  })

  function handleClickRow(id, event) {
    handleClick({
      event,
      link: getRequestProfilePathname({ availableSections, id }),
    })
  }

  function handleMouseDownRow(id, event) {
    handleMouseDown({
      event,
      link: getRequestProfilePathname({ availableSections, id }),
    })
  }

  const exportOptions = getExportOptions({
    permissions,
    selectAllPages,
    selectedIds,
    t,
  })

  const columnResizeConfig = {
    ...TABLE_COLUMN_RESIZE_CONFIG,
    [REQUEST_NO_COLUMN_KEY]: {
      [TABLE_COLUMN_RESIZE_CONFIG_MAX_KEY]: TABLE_COLUMN_MAX_WIDTH,
      [TABLE_COLUMN_RESIZE_CONFIG_MIN_KEY]: TABLE_COLUMN_MIN_WIDTH_SMALL,
    },
  }

  function handleApply(obj = EMPTY_OBJECT) {
    if (!onApplyTableConfig) {
      return Promise.resolve()
    }

    return onApplyTableConfig({
      ...obj,
      columns: obj.columns || columns,
      preview: obj.preview || preview,
      show_extra_info: obj.show_extra_info || isDense,
      view: obj.view || view,
    })
  }

  function handleDensityChange() {
    handleSetDense(!isDense)

    onApplyTableConfig?.({
      columns,
      preview,
      show_extra_info: !isDense,
      view,
    })
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  const { isPerformingMassAction, setItemAction, setMassAction } =
    useRequestTableActions({
      excludeIds,
      filterValues: filterValuesWithExtra,
      pageItems: items?.items,
      pagination: asyncTable.pagination,
      selectAllPages,
      selected,
      selectedIds,
      sub: massActionsSub,
      onCleanSelection: handleCleanSelection,
      onCloseModal,
      onReload: handleReloadAfterMassActionWithActiveItem,
      onSetModal: setModal,
    })

  const { handleOpenExportModal } = useRequestTableHandleExport({
    excludeIds,
    filterValues: filterValuesWithExtra,
    selectAllPages,
    selectedIds,
    sub: massActionsSub,
    unselectAllPages: handleUnselectAllPages,
    onCloseModal,
    onSetModal: setModal,
  })

  const { handleClickFileTag } = useRequestTableItemAttachedFiles({
    onAddNotification: handleAddNotification,
    onCloseModal,
    onReload: handleReloadWithActiveItem,
    onSetModal: setModal,
  })

  const { handleClickLinkedRequestTag, tagModal } =
    useRequestTableAttachedRequests({
      requests: items,
      onCloseModal,
      onReload: handleReloadWithActiveItem,
      onSetModal: setModal,
    })

  const { docFlowTagModal, handleClickDocFlowTag } = useRequestTableDocFlows({
    onCloseModal,
    onReload: handleReloadWithActiveItem,
    onSetModal: setModal,
  })

  return {
    ...asyncTable,
    ...selection,
    availableSections,
    canConnectDocFlow,
    canCreateDocFlow,
    columnResizeConfig,
    docFlowTagModal,
    exportOptions,
    extra,
    filterValuesWithExtra,
    handleApply,
    handleChangeExtraEnabled,
    handleChangeShouldReloadFilters,
    handleChangeVisibility,
    handleClickDocFlowTag,
    handleClickEmailTag,
    handleClickFileTag,
    handleClickLinkedRequestTag,
    handleClickRow,
    handleConfigOpen,
    handleDensityChange,
    handleMouseDownRow,
    handleOpenExportModal,
    handleReload,
    handleReloadExtra,
    isConfigOpen,
    isPerformingMassAction,
    massActions,
    pageContentRef,
    pageScrollWrapperRef,
    refreshButtonProps,
    setConfigOpen,
    setItemAction,
    setMassAction,
    shouldReloadFilters,
    tagModal,
  }
}
